import { useMemo, useState } from "react";
import { string, func, object, arrayOf, shape, bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { ErrorCode, useDropzone } from "react-dropzone";
import { union } from "lodash";
import { hashCode } from "@repo/utils";
import { AI_ATTACHMENT_MB, AI_ATTACHMENT_SIZE, formatFileName } from "@utils";
import { useActiveProjectStore } from "@hooks";
import {
  Autocomplete,
  LevelCheckbox,
  ControlledCheckbox,
  ControlledTextField,
  InfoLabel, TagList, ClearButton, Alert
} from "@components";
import { Collapse, Grid, IconButton, Tooltip } from "@material-ui/core";
import { Attachment, ExpandLess, ExpandMore } from "@material-ui/icons";

export const AICreator = observer(({
  control,
  setValue,
  setError,
  watch,
  addCustomWorkType,
  workTypesLoading,
  languageOptions,
}) => {
  const { t } = useTranslation();
  const store = useActiveProjectStore();
  
  const [ uploadAlertTitle, setUploadAlertTitle ] = useState();
  
  const loadedFiles = watch("promptFiles");
  
  const {
    availableWorkTypes,
    projectWorkTypes,
    useAdvancedAISettings,
  } = store;
  
  const acceptedFormats = useMemo(() => ({
    "application/pdf": [".pdf"],
    "application/vnd.ms-excel": [".xls"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    // "image/*": [".png", ".jpeg", ".jpg"],
  }), []);
  
  const maxFiles = useMemo(() => 4 - loadedFiles.length, [loadedFiles]);
  
  const handleFilesAdd = async (files) => {
    if (files.length ) {
      const fileCodes = files.map((f) => hashCode(f.name))
      setValue("promptFiles", [
        ...loadedFiles.filter(f => !fileCodes.includes(hashCode(f.name))),
        ...files,
      ]);
    }
    return [];
  };
  
  const handleFileRemove = async (fileCode) => {
    if (loadedFiles.length )
      setValue("promptFiles", loadedFiles.filter(f => hashCode(f.name) !== fileCode));
  };
  
  const handleFileError = (message) => {
    setError("promptFiles", { type: "custom", message })
  }
  
  const handleFileReject = (reason) => {
    if(reason?.[0].errors) {
      const errorCodes = reason[0].errors.map(err => err.code);
      let errorMessage;
      if(errorCodes.includes(ErrorCode.FileInvalidType))
        errorMessage = t(`errors.file.wrong_format.multiple`, {type: union(...Object.values(acceptedFormats)).join(", ")})
      else if(errorCodes.includes(ErrorCode.TooManyFiles))
        errorMessage = t("errors.file.too_many", { amount: maxFiles });
      else
        errorMessage = t("errors.file.too_large", { mb: AI_ATTACHMENT_MB });
      // const errorMessage = errorCodes.includes(ErrorCode.FileInvalidType)
      //   ? t(`errors.file.wrong_format.multiple`, {type: uniq(...Object.values(acceptedFormats)).join(", ")})
      //   : t("errors.file.too_large", { mb: AI_ATTACHMENT_SIZE });

      if(setError) {
        handleFileError(errorMessage);
        return;
      }

      setUploadAlertTitle(errorMessage);
    }
  }
  
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: loadedFiles.length >= 4,
    maxFiles,
    maxSize: AI_ATTACHMENT_SIZE,
    onDropAccepted: handleFilesAdd,
    onDropRejected: handleFileReject,
    accept: acceptedFormats,
  });
  
  const handleWorkTypesChange = (formChange) => (workTypes, _, removedWorkType) => {
    formChange(workTypes);
    store.setProjectWorkTypes(workTypes);
    if(removedWorkType && (removedWorkType < 0 || typeof removedWorkType === "string")) {
      store.removeWorkType(removedWorkType);
    }
  };
  
  return (
    <Grid
      item
      container
      direction="column"
      wrap="nowrap"
    >
      <InfoLabel spacing={8} label="views.new_project.name" />
      <ControlledTextField
        control={control}
        name="name"
        placeholder={t("views.new_project.enter_name")}
      />
      
      <InfoLabel
        spacing={8}
        label="views.new_project.prompt"
        tooltipText="views.new_project.prompt_info"
      />
      <Grid
        item container
        {...getRootProps({ className: "dropzone" })}
      >
        
        <ControlledTextField
          control={control}
          name="aiPrompt"
          multiline
          placeholder={t("views.new_project.enter_prompt")}
          className="mb-2"
          minRows={2}
        />
        
        <Grid item container direction="column" alignItems="flex-start" className="mb-2">
          <input
            { ...getInputProps() }
            className="display-block h-0 w-full"
            tabIndex="0"
            aria-label={t("views.new_project.proposal_pdf_upload")}
            name={name}
          />
          <TagList
            tags={loadedFiles.map((a) => ({
              id: hashCode(a.name),
              name: formatFileName(a.name),
            }))}
            onRemove={handleFileRemove}
          />
          <Tooltip title={t("views.new_project.upload_files_full")}>
            <ClearButton
              color="primary"
              onClick={open}
              disabled={loadedFiles.length >= 4}
              icon={<Attachment />}
            >
              { t("views.new_project.upload_files") }
            </ClearButton>
          </Tooltip>
          <Alert
            title={uploadAlertTitle}
            isOpen={!!uploadAlertTitle}
            acceptText={t("common.close")}
            onAccept={() => setUploadAlertTitle(null)}
          />
        </Grid>
        
      </Grid>
      
      <InfoLabel
        spacing={8}
        label="common.work_type"
        tooltipText="views.new_project.work_type_desc"
      />
      <Controller
        name="workTypes"
        control={control}
        render={({
          field: { onChange, name },
          fieldState: { error },
        }) => (
          <Autocomplete
            value={projectWorkTypes}
            options={availableWorkTypes}
            fullWidth
            multiple
            idKey="id"
            labelKey="name"
            onChange={handleWorkTypesChange(onChange)}
            onOptionCreate={addCustomWorkType}
            name={name}
            dataLoading={workTypesLoading}
            error={!!error}
            helperText={error?.message}
            inputProps={{ className: "mb-0" }}
            inputPlaceholder={t("views.new_project.enter_system")}
            getOptionDisabled={() => projectWorkTypes.length >= 10}
          />
        )}
      />
      
      <InfoLabel
        spacing={8}
        label="common.advanced_settings"
        titleActionStart={
          <IconButton
            size="small"
            onClick={() => store.setUseAdvancedAISettings(!useAdvancedAISettings)}
          >
            {useAdvancedAISettings ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      />
      
      <Collapse
        in={useAdvancedAISettings}
        mountOnEnter unmountOnExit
      >
        <Grid item container className="mb-4 px-6">
          <InfoLabel label="views.new_project.ai_creator.language"/>
          <Controller
            name="generatorLanguage"
            control={control}
            render={({
              field: { onChange, name, value },
              fieldState: { error },
            }) => (
              <Autocomplete
                value={value}
                options={languageOptions}
                fullWidth disableClearable
                idKey="id"
                labelKey="name"
                onChange={onChange}
                name={name}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          
          <InfoLabel label="views.new_project.ai_creator.generate" spacing={8} />
          <Grid item container spacing={2} className="mb-2">
            <Grid item xs={4}>
              <ControlledCheckbox
                control={control}
                name="generateScope"
                label={t("views.new_project.ai_creator.generate_scope")}
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledCheckbox
                control={control}
                name="generateDescriptions"
                label={t("views.new_project.ai_creator.generate_descriptions")}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledCheckbox
                control={control}
                name="generateEstimates"
                label={t("views.new_project.ai_creator.generate_estimates")}
              />
            </Grid>
          </Grid>
          
          <InfoLabel label="views.new_project.ai_creator.level" spacing={8}/>
          <Controller
            name="generatorLevel"
            control={control}
            render={({
              field: { onChange, name, value },
              fieldState: { error },
            }) => (
              [0,1,2,3].map((level, index) => (
                <LevelCheckbox
                  key={`level${level}`}
                  name={`${name}_${level}`}
                  onCheck={onChange}
                  level={level}
                  index={index}
                  checked={value >= level}
                  error={!!error}
                />
              ))
            )}
          />
        </Grid>
      </Collapse>
    </Grid>
  );
});

AICreator.propTypes = {
  control: object.isRequired,
  onClose: func.isRequired,
  setValue: func.isRequired,
  watch: func.isRequired,
  addCustomWorkType: func.isRequired,
  languageOptions: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
  })).isRequired,
  workTypesLoading: bool,
};